import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import axios from 'axios';
import { formatDate } from '../../../utils/dateTime';
import {
  Gavel,
  Search,
  Clock,
  ArrowRight,
  RefreshCw,
  AlertCircle,
  CheckCircle,
  Filter,
} from 'lucide-react';
import { Input } from '../../../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { Button } from '../../../components/ui/button';

// Normalize backend URL to prevent double /api paths
const rawBackendUrl = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');
const BACKEND_URL = rawBackendUrl.replace(/\/api$/, '');
const API = `${BACKEND_URL}/api`;

const statusConfig = {
  created: { label: 'Created', color: 'bg-slate-100 text-slate-700', icon: Clock },
  sent: { label: 'Sent', color: 'bg-emerald-100 text-emerald-700', icon: ArrowRight },
  pending_response: { label: 'Pending Response', color: 'bg-amber-100 text-amber-700', icon: Clock },
  response_received: { label: 'Response Received', color: 'bg-amber-100 text-amber-700', icon: CheckCircle },
  escalated: { label: 'Escalated', color: 'bg-red-100 text-red-700', icon: AlertCircle },
  resolved: { label: 'Resolved', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle },
};

const StaffDisputeQueuePage = () => {
  const { token } = useAuth();
  const [disputes, setDisputes] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [bureauFilter, setBureauFilter] = useState('all');

  const fetchDisputes = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (bureauFilter !== 'all') params.append('bureau', bureauFilter);
      params.append('limit', '50');

      const response = await axios.get(`${API}/portal/staff/queue/disputes?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDisputes(response.data.items || []);
      setTotal(response.data.total || 0);
    } catch (err) {
      console.error('Failed to fetch disputes:', err);
      setError(err.response?.data?.detail || 'Failed to load disputes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) fetchDisputes();
  }, [token, statusFilter, bureauFilter]);

  const filteredDisputes = disputes.filter(d => {
    if (!searchTerm) return true;
    const clientName = d.client_name?.toLowerCase() || '';
    const disputeId = d.dispute_id?.toLowerCase() || '';
    return clientName.includes(searchTerm.toLowerCase()) || 
           disputeId.includes(searchTerm.toLowerCase());
  });

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
        {error}
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="staff-dispute-queue">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-stone-900">Dispute Queue</h1>
          <p className="text-slate-500 mt-1">Active disputes requiring attention</p>
        </div>
        <Button onClick={fetchDisputes} variant="outline" size="sm">
          <RefreshCw size={16} className="mr-2" />
          Refresh
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="flex-1 min-w-[200px] relative">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            placeholder="Search by client or dispute ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-44">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="created">Created</SelectItem>
            <SelectItem value="sent">Sent</SelectItem>
            <SelectItem value="pending_response">Pending Response</SelectItem>
            <SelectItem value="response_received">Response Received</SelectItem>
            <SelectItem value="escalated">Escalated</SelectItem>
          </SelectContent>
        </Select>
        <Select value={bureauFilter} onValueChange={setBureauFilter}>
          <SelectTrigger className="w-36">
            <SelectValue placeholder="Bureau" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Bureaus</SelectItem>
            <SelectItem value="TransUnion">TransUnion</SelectItem>
            <SelectItem value="Experian">Experian</SelectItem>
            <SelectItem value="Equifax">Equifax</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Stats */}
      <div className="flex items-center gap-4 text-sm text-slate-500">
        <span>Showing {filteredDisputes.length} of {total} disputes</span>
      </div>

      {/* Disputes Table */}
      {loading ? (
        <div className="bg-white rounded-xl border border-slate-200 p-8">
          <div className="animate-pulse space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-16 bg-slate-100 rounded" />
            ))}
          </div>
        </div>
      ) : filteredDisputes.length > 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase">Client</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase">Bureau</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase">Items</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase">Status</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase">Round</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase">Created</th>
                <th className="text-right px-4 py-3 text-xs font-medium text-slate-500 uppercase">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredDisputes.map((dispute) => {
                const status = statusConfig[dispute.dispute_status] || statusConfig.created;
                const StatusIcon = status.icon;
                return (
                  <tr key={dispute.dispute_id} className="hover:bg-slate-50" data-testid={`dispute-row-${dispute.dispute_id}`}>
                    <td className="px-4 py-3">
                      <p className="font-medium text-stone-900">{dispute.client_name || 'Unknown'}</p>
                      <p className="text-xs text-slate-500">{dispute.case_id}</p>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-slate-700">{dispute.bureau || '—'}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-slate-700">{dispute.items?.length || 0} items</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${status.color}`}>
                        <StatusIcon size={12} />
                        {status.label}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-slate-700">Round {dispute.round || 1}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-slate-500">
                        {formatDate(dispute.created_at, { fallback: '—' })}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Link 
                        to={`/staff/cases/${dispute.case_id}/disputes/${dispute.dispute_id}`}
                        className="text-emerald-600 hover:text-emerald-700 text-sm font-medium"
                      >
                        View
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
          <Gavel size={48} className="mx-auto text-slate-300 mb-4" />
          <h3 className="text-lg font-semibold text-stone-900 mb-2">No active disputes</h3>
          <p className="text-slate-500">
            {searchTerm || statusFilter !== 'all' || bureauFilter !== 'all'
              ? 'Try adjusting your filters'
              : 'All disputes are resolved or closed'}
          </p>
        </div>
      )}
    </div>
  );
};

export default StaffDisputeQueuePage;
